import type { RequestScenario } from '@/types/admin'

const MESSAGES: Record<RequestScenario, string> = {
  normal: '操作已完成',
  timeout: '请求超时，请检查网络后重试',
  cancel: '请求已取消',
  '401': '登录状态已过期，请重新登录',
  '403': '当前角色无权执行此操作',
  '404': '请求的业务数据不存在或已归档',
  '409': '数据已被其他操作更新，请刷新后重试',
  '422': '提交内容未通过校验，请检查标注字段',
  '429': '操作过于频繁，请稍后再试',
  '500': '服务暂时不可用，请稍后重试',
  offline: '网络连接已断开，已保留当前页面',
}

export interface ErrorDetails {
  /** Business conflicts keep enough context for a recoverable follow-up UI. */
  conflict?: {
    entity: string
    entityId: string
    entityName: string
    resource: 'region' | 'regions' | 'links' | 'assets' | 'masters' | 'templates' | 'content' | 'membership_plans'
    references: Array<{ module: string; label: string; count: number }>
    message: string
    recoverTo?: string
  }
  [key: string]: unknown
}

export class ApiError extends Error {
  readonly code: RequestScenario | 'validation'
  readonly status: number
  readonly traceId?: string
  readonly fieldErrors?: Record<string, string>
  readonly details?: ErrorDetails

  constructor(
    code: RequestScenario | 'validation',
    message?: string,
    fieldErrors?: Record<string, string>,
    details?: ErrorDetails,
  ) {
    super(message || MESSAGES[code as RequestScenario] || '操作未完成')
    this.name = 'ApiError'
    this.code = code
    this.status = /^\d{3}$/.test(code) ? Number(code) : 0
    this.traceId = `REQ-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${Math.floor(Math.random() * 9000 + 1000)}`
    this.fieldErrors = fieldErrors
    this.details = details
  }
}

export function isCancel(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'cancel'
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message
  if (error instanceof Error) return error.message
  return '操作未完成，请稍后重试'
}

/** Explicit business conflict used by the in-memory adapter. */
export class BusinessConflict extends ApiError {
  constructor(
    message: string,
    conflict: NonNullable<ErrorDetails['conflict']>,
  ) {
    super('409', message, undefined, { conflict })
    this.name = 'BusinessConflict'
  }
}
